import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useDiet } from "@/contexts/DietContext";
import { motion } from "framer-motion";
import { User, Mail, Activity, Heart, Award, Flame, MessageCircle, Lock, Pencil, Check, X, Copy, Receipt, ChevronRight, Crown, XCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { achievements, TOTAL_ACHIEVEMENTS } from "@/components/diary/AchievementsSheet";
import type { AchievementContext } from "@/components/diary/AchievementsSheet";

const EMAIL_CHANGE_AUTH_KEY = "email_change_authorized_until";
const EMAIL_CHANGE_AUTH_WINDOW_MS = 10 * 60 * 1000;

const getBmiStatus = (bmi: number) => {
  if (bmi < 18.5) return { label: "Abaixo do peso", color: "text-warning" };
  if (bmi < 25) return { label: "Peso normal", color: "text-primary" };
  if (bmi < 30) return { label: "Sobrepeso", color: "text-warning" };
  return { label: "Obesidade", color: "text-destructive" };
};

interface AchievementsData {
  totalEntries: number;
  streak: number;
  currentStreak: number;
  uniqueDays: number;
  daysOnTarget: number;
  consecutiveDaysOnTarget: number;
  mealTypesUsed: string[];
  hasPhoto: boolean;
  entriesWithPhoto: number;
  totalMetricEntries: number;
  metricsWithPhotos: number;
  measuredBodyParts: number;
  weightVariation: boolean;
}

const Profile = () => {
  const { user, updateName, requestEmailChangeLink, updateEmail } = useAuth();
  const { measures, refreshSubscription } = useDiet();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [achievementsData, setAchievementsData] = useState<AchievementsData | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    apiFetch<{ has_active_subscription: boolean }>("api-subscription", undefined, { method: "GET" })
      .then((d) => setHasActiveSubscription(!!d.has_active_subscription))
      .catch(() => {});
  }, []);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [canChangeEmail, setCanChangeEmail] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [sendingEmailRequest, setSendingEmailRequest] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    setNameValue(user.name);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const params = new URLSearchParams(window.location.search);
    const hasAuthParam = params.get("email_change_auth") === "1";
    const now = Date.now();
    const storedUntil = Number(sessionStorage.getItem(EMAIL_CHANGE_AUTH_KEY) || "0");

    if (hasAuthParam) {
      const expiresAt = now + EMAIL_CHANGE_AUTH_WINDOW_MS;
      sessionStorage.setItem(EMAIL_CHANGE_AUTH_KEY, String(expiresAt));
      setCanChangeEmail(true);
      setEmailValue("");

      params.delete("email_change_auth");
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}`;
      window.history.replaceState({}, "", nextUrl);

      toast({
        title: "Email atual confirmado",
        description: "Agora informe o novo email para receber a confirmação final.",
      });
      return;
    }

    setCanChangeEmail(storedUntil > now);
  }, [user, toast]);

  const bmi = measures ? measures.weight / ((measures.height / 100) ** 2) : 0;
  const bmiStatus = getBmiStatus(bmi);

  const loadAchievements = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiFetch("api-diary", { action: "achievements" });
      setAchievementsData(res.data);
    } catch (e) {
      console.error("Failed to load achievements:", e);
    }
  }, [user]);

  useEffect(() => {
    loadAchievements();
  }, [loadAchievements]);

  const currentStreak = achievementsData?.currentStreak || 0;
  const bestStreak = achievementsData?.streak || 0;

  const context = useMemo<AchievementContext>(() => {
    if (!achievementsData) {
      return {
        totalEntries: 0,
        streak: 0,
        uniqueDays: 0,
        daysOnTarget: 0,
        consecutiveDaysOnTarget: 0,
        mealTypesUsed: new Set(),
        hasPhoto: false,
        entriesWithPhoto: 0,
        totalMetricEntries: 0,
        metricsWithPhotos: 0,
        measuredBodyParts: 0,
        weightVariation: false,
        totalWaterDays: 0,
        waterOnTargetDays: 0,
        consecutiveWaterDays: 0,
        waterStreak: 0,
      };
    }
    return {
      totalEntries: achievementsData.totalEntries,
      streak: achievementsData.streak,
      uniqueDays: achievementsData.uniqueDays,
      daysOnTarget: achievementsData.daysOnTarget,
      consecutiveDaysOnTarget: achievementsData.consecutiveDaysOnTarget || 0,
      mealTypesUsed: new Set(achievementsData.mealTypesUsed),
      hasPhoto: achievementsData.hasPhoto,
      entriesWithPhoto: achievementsData.entriesWithPhoto,
      totalMetricEntries: achievementsData.totalMetricEntries || 0,
      metricsWithPhotos: achievementsData.metricsWithPhotos || 0,
      measuredBodyParts: achievementsData.measuredBodyParts || 0,
      weightVariation: achievementsData.weightVariation || false,
      totalWaterDays: (achievementsData as any).totalWaterDays || 0,
      waterOnTargetDays: (achievementsData as any).waterOnTargetDays || 0,
      consecutiveWaterDays: (achievementsData as any).consecutiveWaterDays || 0,
      waterStreak: (achievementsData as any).waterStreak || 0,
    };
  }, [achievementsData]);

  const unlockedCount = useMemo(() => achievements.filter((a) => a.check(context)).length, [context]);

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === user?.name) {
      setEditingName(false);
      return;
    }

    setSavingName(true);
    const result = await updateName(trimmed);
    setSavingName(false);

    if (result.error) {
      toast({ title: "Erro ao atualizar nome", description: result.error, variant: "destructive" });
      return;
    }

    toast({ title: "Nome atualizado com sucesso!" });
    setEditingName(false);
  };

  const handleRequestEmailChange = async () => {
    setSendingEmailRequest(true);
    const result = await requestEmailChangeLink();
    setSendingEmailRequest(false);

    if (result.error) {
      toast({ title: "Erro ao solicitar alteração de email", description: result.error, variant: "destructive" });
      return;
    }

    toast({
      title: "Solicitação enviada",
      description: "Abra o link enviado para seu email atual para autenticar a troca.",
    });
  };

  const handleCancelEmailFlow = () => {
    sessionStorage.removeItem(EMAIL_CHANGE_AUTH_KEY);
    setCanChangeEmail(false);
    setEmailValue("");
  };

  const handleSaveEmail = async () => {
    const trimmed = emailValue.trim().toLowerCase();

    if (!trimmed) {
      toast({ title: "Informe o novo email", variant: "destructive" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "Email inválido", variant: "destructive" });
      return;
    }

    if (trimmed === user?.email) {
      toast({ title: "O novo email deve ser diferente do atual", variant: "destructive" });
      return;
    }

    setSavingEmail(true);
    const result = await updateEmail(trimmed);
    setSavingEmail(false);

    if (result.error) {
      toast({ title: "Erro ao solicitar troca de email", description: result.error, variant: "destructive" });
      return;
    }

    sessionStorage.removeItem(EMAIL_CHANGE_AUTH_KEY);
    setCanChangeEmail(false);
    setEmailValue("");

    toast({
      title: "Confirmação enviada",
      description: "Verifique o novo email para concluir a alteração.",
    });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Profile Hero Card */}
      <div className="bg-card rounded-2xl shadow-card border border-border overflow-hidden">
        {/* Gradient banner */}
        <div className="h-20 gradient-primary relative">
          <div className="absolute -bottom-8 left-6">
            <div className="w-16 h-16 rounded-2xl bg-card border-4 border-card flex items-center justify-center shadow-lg">
              <User className="w-7 h-7 text-primary" />
            </div>
          </div>
        </div>

        <div className="pt-12 px-6 pb-5 space-y-4">
          {/* Name + Email */}
          <div className="space-y-1">
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  className="h-8 text-sm"
                  autoFocus
                  maxLength={100}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                />
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={handleSaveName} disabled={savingName}>
                  <Check className="w-4 h-4 text-primary" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0"
                  onClick={() => {
                    setEditingName(false);
                    setNameValue(user?.name || "");
                  }}
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h1 className="text-xl font-bold text-foreground truncate">{user?.name}</h1>
                <button onClick={() => setEditingName(true)} className="opacity-0 group-hover:opacity-100 transition-opacity" title="Editar nome">
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                </button>
              </div>
            )}

            <p className="text-muted-foreground flex items-center gap-1 text-sm truncate">
              <Mail className="w-3.5 h-3.5 shrink-0" /> {user?.email}
            </p>
          </div>

          {/* Email change */}
          {!canChangeEmail ? (
            <Button variant="outline" size="sm" onClick={handleRequestEmailChange} disabled={sendingEmailRequest}>
              Solicitar alteração de email
            </Button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Email atual autenticado. Informe o novo email:</p>
              <div className="flex items-center gap-2">
                <Input
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  className="h-8 text-sm"
                  type="email"
                  autoFocus
                  maxLength={255}
                  placeholder="novo@email.com"
                  onKeyDown={(e) => e.key === "Enter" && handleSaveEmail()}
                />
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={handleSaveEmail} disabled={savingEmail}>
                  <Check className="w-4 h-4 text-primary" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={handleCancelEmailFlow}>
                  <X className="w-4 h-4 text-muted-foreground" />
                </Button>
              </div>
            </div>
          )}

          {/* Quick actions inside card */}
          <div className="border-t border-border pt-4 space-y-1">
            <button
              onClick={() => navigate("/purchase-history")}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/60 transition-colors group"
            >
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Receipt className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">Histórico de Compras</p>
                <p className="text-xs text-muted-foreground">Veja seus planos e transações</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </button>

            <button
              onClick={() => navigate("/plans")}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/60 transition-colors group"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                <Crown className="w-4 h-4 text-amber-500" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">Meu Plano</p>
                <p className="text-xs text-muted-foreground">Gerencie sua assinatura</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </button>

            {hasActiveSubscription && (
              <button
                onClick={() => setCancelOpen(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-destructive/5 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                  <XCircle className="w-4 h-4 text-destructive" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-destructive">Cancelar assinatura</p>
                  <p className="text-xs text-muted-foreground">Encerrar a renovação automática do PRO FIT MAX</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-destructive transition-colors" />
              </button>
            )}

            <button
              onClick={() => setSupportOpen(true)}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/60 transition-colors group"
            >
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <MessageCircle className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">Falar com suporte</p>
                <p className="text-xs text-muted-foreground">Tire dúvidas ou peça ajuda</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </button>
          </div>
        </div>
      </div>

      {measures && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-card rounded-xl p-4 shadow-card border border-border">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Activity className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium">Peso</span>
            </div>
            <p className="text-lg font-bold text-foreground">{measures.weight} kg</p>
          </div>
          <div className="bg-card rounded-xl p-4 shadow-card border border-border">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Activity className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium">Altura</span>
            </div>
            <p className="text-lg font-bold text-foreground">{measures.height} cm</p>
          </div>
          <div className="bg-card rounded-xl p-4 shadow-card border border-border">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Heart className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium">IMC</span>
            </div>
            <p className="text-lg font-bold text-foreground">{bmi.toFixed(1)}</p>
            <p className={`text-xs font-medium ${bmiStatus.color}`}>{bmiStatus.label}</p>
          </div>
          <div className="bg-card rounded-xl p-4 shadow-card border border-border">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Flame className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium">Calorias</span>
            </div>
            <p className="text-lg font-bold text-foreground">{measures.calories}</p>
            <p className="text-xs text-muted-foreground">kcal/dia</p>
          </div>
        </div>
      )}

      <div className="bg-card rounded-2xl p-5 shadow-card border border-border">
        <div className="flex items-center gap-2 mb-3">
          <Flame className="w-5 h-5 text-primary" />
          <h2 className="font-semibold text-foreground">Sequência Atual</h2>
        </div>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5, 6, 7].map((d) => (
            <div
              key={d}
              className={`flex-1 h-8 rounded-lg flex items-center justify-center text-xs font-medium ${
                d <= currentStreak ? "gradient-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}
            >
              {d}
            </div>
          ))}
          {currentStreak > 7 && (
            <div className="flex-1 h-8 rounded-lg flex items-center justify-center text-xs font-bold gradient-primary text-primary-foreground">
              +{currentStreak - 7}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <p className="text-sm text-muted-foreground">
            {currentStreak === 0
              ? "Registre refeições no diário para iniciar sua sequência!"
              : `${currentStreak} dia${currentStreak > 1 ? "s" : ""} consecutivo${currentStreak > 1 ? "s" : ""}`}
          </p>
          {bestStreak > 0 && (
            <p className="text-xs text-muted-foreground/70">
              🏆 Recorde: {bestStreak} dia{bestStreak > 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>

      <div className="bg-card rounded-2xl p-5 shadow-card border border-border">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            <h2 className="font-semibold text-foreground">Conquistas</h2>
          </div>
          <span className="text-xs text-muted-foreground">{unlockedCount}/{TOTAL_ACHIEVEMENTS}</span>
        </div>
        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mb-4">
          <motion.div
            className="h-full rounded-full gradient-primary"
            initial={{ width: 0 }}
            animate={{ width: `${(unlockedCount / TOTAL_ACHIEVEMENTS) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {achievements.map((a) => {
            const isUnlocked = a.check(context);
            return (
              <div
                key={a.id}
                className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${
                  isUnlocked ? "border-primary/30 bg-primary/5" : "border-border opacity-50"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg ${isUnlocked ? "bg-primary/10" : "bg-muted"}`}>
                  {isUnlocked ? a.emoji : <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold truncate ${isUnlocked ? "text-foreground" : "text-muted-foreground"}`}>
                    {a.title}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">{a.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Suporte</DialogTitle>
            <DialogDescription>
              Entre em contato conosco pelo email abaixo:
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
            <Mail className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm font-medium text-foreground select-all">suporte.nutrircorpoemente@gmail.com</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0 ml-auto"
              onClick={() => {
                navigator.clipboard.writeText("suporte.nutrircorpoemente@gmail.com");
                toast({ title: "Email copiado!" });
              }}
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar assinatura PRO FIT MAX?</AlertDialogTitle>
            <AlertDialogDescription>
              Você não será mais cobrado nos próximos meses. Seu acesso aos recursos do plano continua válido até o fim do período já pago.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={canceling}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              disabled={canceling}
              onClick={async (e) => {
                e.preventDefault();
                setCanceling(true);
                try {
                  await apiFetch("cancel-subscription", undefined, { method: "POST" });
                  toast({ title: "Assinatura cancelada", description: "Seu acesso continua até o fim do período já pago." });
                  setHasActiveSubscription(false);
                  await refreshSubscription?.();
                  setCancelOpen(false);
                } catch (err: any) {
                  toast({ title: "Erro ao cancelar assinatura", description: err?.message || "Tente novamente.", variant: "destructive" });
                } finally {
                  setCanceling(false);
                }
              }}
            >
              {canceling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelando...</> : "Cancelar assinatura"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default Profile;
